import { useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { type FileUploadStatus, useManagedFileUpload } from './useManagedFileUpload';

export type { FileUploadStatus };

export function useFileUpload() {
  const getUploadUrls = trpc.files.getUploadUrls.useMutation();
  const registerFile = trpc.files.create.useMutation();
  const cancelRemoteUpload = trpc.files.cancelUpload.useMutation();

  const requestUploadUrls = useCallback(
    (files: File[]) =>
      getUploadUrls.mutateAsync({
        files: files.map((file) => ({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type || 'application/octet-stream',
        })),
      }),
    [getUploadUrls]
  );
  const registerUploadedFile = useCallback(
    async (file: File, r2Path: string, category?: string) => {
      const result = await registerFile.mutateAsync({
        category,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/octet-stream',
        r2Path,
      });
      if (!result.success || !result.file) throw new Error('Failed to register file in database');
      return result.file.id;
    },
    [registerFile]
  );
  const cleanupUpload = useCallback(
    async (r2Path: string) => {
      await cancelRemoteUpload.mutateAsync({ r2Path });
    },
    [cancelRemoteUpload]
  );

  return useManagedFileUpload({
    cleanupUpload,
    getUploadUrls: requestUploadUrls,
    isRequestingUploadUrls: getUploadUrls.isPending,
    registerFile: registerUploadedFile,
  });
}
